// Category review: suggestions to accept or change, rules ("always do
// this"), transfer and intercompany matches, and settings. Viewers see
// everything read-only.
import React, { useCallback, useEffect, useState } from 'react';
import { categoriesAPI } from '../services/api';
import { autocatAPI, SOURCE_LABEL, applyConfirmText } from '../features/autocat';
import { readSavedBusiness, ALL } from '../utils/businessSelection';
import { formatApiError } from '../utils/errorMessage';

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const TABS = [['review', 'To review'], ['rules', 'Rules'], ['transfers', 'Transfers'], ['intercompany', 'Intercompany'], ['settings', 'Settings']];

export default function CategoryReview({ selectedBusiness }) {
  const saved = selectedBusiness ?? readSavedBusiness();
  const b = saved && saved !== ALL ? Number(saved) : null;
  const [tab, setTab] = useState('review');
  const [settings, setSettings] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [rules, setRules] = useState([]);
  const [matches, setMatches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [always, setAlways] = useState({});
  const [notAvailable, setNotAvailable] = useState(false);
  const canAct = settings?.can_act;

  const load = useCallback(async () => {
    if (!b) return;
    try {
      const s = (await autocatAPI.settings(b)).data;
      setSettings(s);
      const c = await categoriesAPI.getAll().catch(() => ({ data: [] }));
      setCategories(Array.isArray(c.data) ? c.data : (c.data.categories || []));
      if (tab === 'review') { const q = (await autocatAPI.queue(b)).data; setRows(q.rows); setTotal(q.total); }
      if (tab === 'rules') setRules((await autocatAPI.rules(b)).data.rules);
      if (tab === 'transfers' || tab === 'intercompany') setMatches((await autocatAPI.transfers(b, tab === 'intercompany' ? 'intercompany' : 'transfer')).data.matches);
    } catch (e) {
      if (e?.response?.status === 404) setNotAvailable(true);
      else setMessage(formatApiError(e, 'Could not load category review'));
    }
  }, [b, tab]);

  useEffect(() => { load(); }, [load]);

  const run = async (fn, done) => {
    setBusy(true); setMessage('');
    try { const r = await fn(); if (done) setMessage(done(r)); await load(); } catch (e) { setMessage(formatApiError(e, 'Something went wrong')); }
    setBusy(false);
  };

  if (notAvailable) return <div style={{ padding: 24 }}><h1>Category review</h1><p>Auto-categorization is not turned on yet.</p></div>;
  if (!b) return <div style={{ padding: 24 }}><h1>Category review</h1><p>Pick a business first.</p></div>;

  const applyRule = (rule) => run(async () => {
    const p = (await autocatAPI.previewRule(b, rule.id)).data;
    if (!p.count) return { data: { updated: 0 } };
    if (!window.confirm(applyConfirmText(p))) return { data: { updated: 0 } };
    return autocatAPI.applyRule(b, rule.id, p.count);
  }, (r) => `${r.data.updated} row(s) updated.`);

  return (
    <div style={{ padding: 24 }}>
      <h1>Category review</h1>
      {settings && !settings.enabled && <p style={{ color: '#b45309' }}>Auto-categorization is off for this business. {settings.can_change_settings ? 'Turn it on under Settings.' : ''}</p>}
      {!canAct && settings && <p style={{ color: '#6b7280' }}>You can view this page; your role cannot change categories.</p>}
      <div role="tablist" style={{ display: 'flex', gap: 8, margin: '12px 0' }}>
        {TABS.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} style={{ fontWeight: tab === k ? 700 : 400 }}>{label}</button>
        ))}
      </div>
      {message && <p role="status">{message}</p>}

      {tab === 'review' && (
        <div>
          <p>{total} row(s) to review. {canAct && settings?.enabled && <button type="button" disabled={busy} onClick={() => run(() => autocatAPI.run(b), (r) => `Checked: ${r.data.applied} set by your rules or past choices, ${r.data.review} to review, ${r.data.transfers} transfer match(es).`)}>Check uncategorized rows now</button>}</p>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ textAlign: 'left', color: '#6b7280', fontSize: 13 }}><th>Date</th><th>Description</th><th>Amount</th><th>Suggestion</th><th>Action</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td>{r.transaction_date}</td>
                  <td>{r.description}<div style={{ fontSize: 12, color: '#6b7280' }}>{r.account_name}</div></td>
                  <td>{r.direction === 'in' ? '+' : '−'}{money(r.amount)}</td>
                  <td>
                    {r.transfer_match_id ? <span>Possible transfer (see Transfers)</span> : null}
                    {r.suggested_category_name ? (
                      <span>{r.suggested_category_name} <small style={{ color: '#6b7280' }}>({SOURCE_LABEL[r.suggestion_source] || r.suggestion_source}, {r.suggestion_confidence} confidence)</small></span>
                    ) : (!r.transfer_match_id && <span style={{ color: '#6b7280' }}>No suggestion</span>)}
                  </td>
                  <td>
                    {canAct && (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        {r.suggested_category_id && <button type="button" disabled={busy} onClick={() => run(() => autocatAPI.accept(b, r.id, !!always[r.id]), () => 'Accepted.')}>Accept</button>}
                        <select aria-label={`Category for ${r.description}`} defaultValue="" disabled={busy}
                          onChange={(e) => e.target.value && run(() => autocatAPI.change(b, r.id, Number(e.target.value), !!always[r.id]), () => 'Category set.')}>
                          <option value="">Change to…</option>
                          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <label style={{ fontSize: 12 }}><input type="checkbox" checked={!!always[r.id]} onChange={(e) => setAlways({ ...always, [r.id]: e.target.checked })} /> Always do this</label>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'rules' && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ textAlign: 'left', color: '#6b7280', fontSize: 13 }}><th>When the payee</th><th>Category</th><th>Used</th><th>Made by</th><th /></tr></thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id} style={{ borderTop: '1px solid #f3f4f6', opacity: r.enabled ? 1 : 0.5 }}>
                <td>{r.match_op.replace('_', ' ')} “{r.match_value}”{r.direction ? ` (money ${r.direction})` : ''}</td>
                <td>{r.category_name}</td>
                <td>{r.hits}</td>
                <td>{r.created_by_email || '—'}</td>
                <td>{canAct && r.enabled && (
                  <span style={{ display: 'flex', gap: 6 }}>
                    <button type="button" disabled={busy} onClick={() => applyRule(r)}>Apply to existing rows…</button>
                    <button type="button" disabled={busy} onClick={() => run(() => autocatAPI.disableRule(b, r.id), () => 'Rule turned off.')}>Turn off</button>
                  </span>
                )}</td>
              </tr>
            ))}
            {!rules.length && <tr><td colSpan={5} style={{ color: '#6b7280' }}>No rules yet. Tick “Always do this” when you categorize a row.</td></tr>}
          </tbody>
        </table>
      )}

      {(tab === 'transfers' || tab === 'intercompany') && (
        <div>
          {tab === 'intercompany' && <p style={{ color: '#6b7280' }}>Money that moved between two of your businesses. Review each pair.</p>}
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ textAlign: 'left', color: '#6b7280', fontSize: 13 }}><th>Out</th><th>In</th><th>Amount</th><th /></tr></thead>
            <tbody>
              {matches.map((m) => (
                <tr key={m.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td>{m.out_date} {m.out_account}<div style={{ fontSize: 12 }}>{m.out_description}</div></td>
                  <td>{m.in_date} {m.in_account}<div style={{ fontSize: 12 }}>{m.in_description}</div></td>
                  <td>{money(m.amount)}</td>
                  <td>{canAct && (
                    <span style={{ display: 'flex', gap: 6 }}>
                      <button type="button" disabled={busy} onClick={() => run(() => autocatAPI.decideTransfer(b, m.id, true), () => 'Marked as a transfer.')}>It's a transfer</button>
                      <button type="button" disabled={busy} onClick={() => run(() => autocatAPI.decideTransfer(b, m.id, false), () => 'Not a transfer.')}>Not a transfer</button>
                    </span>
                  )}</td>
                </tr>
              ))}
              {!matches.length && <tr><td colSpan={4} style={{ color: '#6b7280' }}>Nothing to review.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'settings' && settings && (
        <div>
          <label style={{ display: 'block', margin: '8px 0' }}>
            <input type="checkbox" checked={settings.enabled} disabled={!settings.can_change_settings || busy}
              onChange={(e) => run(() => autocatAPI.saveSettings(b, { enabled: e.target.checked }))} /> Auto-categorize new and imported rows for this business
          </label>
          <label style={{ display: 'block', margin: '8px 0' }}>
            <input type="checkbox" checked={settings.learned_auto_apply} disabled={!settings.can_change_settings || busy}
              onChange={(e) => run(() => autocatAPI.saveSettings(b, { learned_auto_apply: e.target.checked }))} /> Also apply a category automatically when you have chosen it at least 3 times for the same payee with no exceptions (you can undo any time)
          </label>
          <p style={{ color: '#6b7280', fontSize: 13 }}>Your rules always apply. Everything else waits here for you. Nothing changes in reconciled or closed periods, and categories you set yourself are never changed.</p>
        </div>
      )}
    </div>
  );
}
